import { Table, TableHead, TableRow, Typography, Button, TableContainer, IconButton, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { useState, useRef, useEffect, useContext, Fragment } from "react";
import five from "../../../src/assets/images/five.png";
import four from "../../../src/assets/images/four.png";
import one from "../../../src/assets/images/one.png";
import three from "../../../src/assets/images/three.png";
import two from "../../../src/assets/images/two.png";

import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useReactToPrint } from "react-to-print";
import Modal from "../Modal/Modal";
import MUITable from "./../../Component/UI/Table/Table";
import SentimentItem from "./SentimentItem";
import classes from "./SentimentTable.module.css";
import { getCleanQuestion } from "../../utils/helpers";
import { getColorForScore } from "../../utils/helpers";
import { DiscoveryContext } from "../../luminaire/BrandAudit/discovery-context";
import { downloadExcel } from "../../utils/monitoring-helpers";
import BaseResponsesModal from "../../Component/BaseResponseComponent/BaseResponsesModal";
import useBaseResponsesModal from "../../Component/BaseResponseComponent/useBaseResponsesModal";

export function trimBeforeInstructions(text) {
  const pattern = /instructions\s*:\s*1\s*\.\s*create\s*a\s*single\s*paragraph\s*of\s*exactly/i;
  const match = text.match(pattern);

  if (match) {
    const index = match.index;
    return text.substring(0, index).trim();
  }

  return text;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#eff4f8",
    color: "#2d3748",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

// Paginated component for reporting mode
function PaginatedSentimentTable({
  data,
  renderRow,
  renderHeader,
  maxPageHeight = 680
}) {
  const [paginatedData, setPaginatedData] = useState([]);
  const [isCalculating, setIsCalculating] = useState(true);
  const measurementRef = useRef(null);

  const sentiments = data?.sentimentMeta || [];

  useEffect(() => {
    if (sentiments.length === 0) {
      setIsCalculating(false);
      return;
    }

    setTimeout(() => {
      calculatePages();
    }, 100);
  }, [sentiments]);

  const calculatePages = () => {
    if (!measurementRef.current) return;

    const rows = measurementRef.current.querySelectorAll('tbody tr');
    const headerHeight = measurementRef.current.querySelector('thead')?.offsetHeight || 60;

    const pages = [];
    let currentPage = [];
    let currentHeight = headerHeight;

    rows.forEach((row, index) => {
      const rowHeight = row.offsetHeight;

      // Increase safety margin
      const safetyMargin = 40; // Increased from 20 to 40

      if (currentHeight + rowHeight + safetyMargin > maxPageHeight && currentPage.length > 0) {
        pages.push([...currentPage]);
        currentPage = [index];
        currentHeight = headerHeight + rowHeight;
      } else {
        currentPage.push(index);
        currentHeight += rowHeight;
      }
    });

    if (currentPage.length > 0) {
      pages.push(currentPage);
    }

    setPaginatedData(pages);
    setIsCalculating(false);
  };

  const renderMeasurementTable = () => (
    <div
      ref={measurementRef}
      style={{
        position: 'absolute',
        visibility: 'hidden',
        width: '1100px',
        padding: '16px'
      }}
    >
      <MUITable
        size="medium"
        elevation={0}
        data={sentiments}
        render={renderRow}
        renderHeaders={() => renderHeader(sentiments)}
      />
    </div>
  );

  const renderPageTable = (pageIndices, pageNumber, totalPages) => {
    const pageData = pageIndices.map(index => sentiments[index]);

    return (
      <div
        key={pageNumber}
        className="sentiment-page"
        style={{
          pageBreakAfter: pageNumber < totalPages - 1 ? 'always' : 'auto',
          marginBottom: pageNumber < totalPages - 1 ? '30px' : '0',
          width: '1100px',
          minHeight: `${maxPageHeight}px`,  // 👈 ADD THIS - ensures consistent height
          maxHeight: `${maxPageHeight}px`,
          border: '1px solid #e0e0e0',
          padding: '16px',
          borderRadius: '4px',
          backgroundColor: '#fff'
        }}
      >
        {/* {totalPages > 1 && (
          <div style={{
            textAlign: 'right',
            fontSize: '12px',
            color: '#666',
            marginBottom: '8px',
            fontWeight: 'bold'
          }}>
            Page {pageNumber + 1} of {totalPages}
          </div>
        )} */}

        <MUITable
          size="medium"
          elevation={0}
          data={pageData}
          render={(item, localIndex) => {
            const globalIndex = pageIndices[localIndex];
            return renderRow(item, globalIndex);
          }}
          renderHeaders={() => renderHeader(sentiments)}
        />
      </div>
    );
  };

  if (isCalculating) {
    return (
      <>
        {renderMeasurementTable()}
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Typography variant="body1">Calculating page layout...</Typography>
        </div>
      </>
    );
  }

  if (paginatedData.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No data available
      </Typography>
    );
  }

  return (
    <div className="paginated-sentiment-tables">
      {/* <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        This view is optimized for PPT export. Total pages: {paginatedData.length}
      </Typography> */}
      {paginatedData.map((pageIndices, pageIndex) =>
        renderPageTable(pageIndices, pageIndex, paginatedData.length)
      )}
    </div>
  );
}

export default function SentimentTable({
  data,
  showScore = true,
  selectedInput,
  fromDashboardBrandScore = false,
  compIndexScoreMetaData = [],
  brandProduct,
  showInfoIcon = false,
  reportviewButtontext = "Report View",
  // When embedded inside a factor-scoped block (e.g. the Accessibility
  // breakups view), the caller already renders its own "B. Sentiment"
  // heading — suppress this component's own "A. Sentiment Analysis"
  // header/download/report-view chrome, and let the caller override the
  // "Average Sentiment Scores" heading text (e.g. "B.1. Average Sentiment Score").
  embedded = false,
  scoresHeading,
  idSuffix = "",
}) {
  // Read isReporting flag from URL parameters
  const isReporting = new URLSearchParams(window.location.search).get('isReporting') === 'true' || new URLSearchParams(window.location.search).get('reporting') === 'true';
  console.log("isReporting:", isReporting);

  const [showSentimentModal, setShowSentimentModal] = useState(false);
  const [selectedSentiment, setSelectedSentiment] = useState({
    data: null,
    questionNumber: 0,
    datasetNumber: 0,
  });
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [selectedSources, setSelectedSources] = useState({
    question: "",
    sources: [],
    number: 0,
  });

  // New state for reporting modal
  const [showReportingModal, setShowReportingModal] = useState(false);
    const {
    infoModalOpen: sentimentInfoModalOpen,
    infoModalTitle: sentimentInfoModalTitle,
    infoModalContent: sentimentInfoModalContent,
    closeInfoModal: closeSentimentInfoModal,
    handleSentimentQuestionClick,
    handleSentimentAnalysisClick,
  } = useBaseResponsesModal(data, "sentiment");


  const sentiments = data?.sentimentMeta || [];
  const hasFactorColumn = sentiments.some((group) => group?.[0]?.factor);

  const discoveryCtx = useContext(DiscoveryContext);
  const isPropensity = discoveryCtx?.isPropensityToBuy?.() ?? (discoveryCtx?.brand_scores?.rbiType === "propensityToBuy");
  const mapTitle = (t) => discoveryCtx?.mapMetricTitle?.(t) ?? t;

  function getSentimentIcon(llm_sentiment) {
    let sentimentIcon = "";
    switch (llm_sentiment) {
      case "Positive":
      case "Highly Likely":
        sentimentIcon = <img height={30} width={30} src={five} alt="5" />;
        break;
      case "Slightly Positive":
      case "Likely":
        sentimentIcon = <img height={30} width={30} src={four} alt="4" />;
        break;
      case "Neutral":
        sentimentIcon = <img height={30} width={30} src={three} alt="3" />;
        break;
      case "Unlikely":
      case "Slightly Negative":
        sentimentIcon = <img height={30} width={30} src={two} alt="2" />;
        break;
      case "Very Unlikely":
      case "Negative":
        sentimentIcon = <img height={30} width={30} src={one} alt="1" />;
        break;
      default:
        sentimentIcon = "";
    }

    return sentimentIcon;
  }

  function renderDatasetCell(itemSentiment, datasetIdx, questionNumber) {
    return (
      <TableCell align="center" key={datasetIdx}>
        <span
          className={classes.sentiment_emoji}
          onClick={() => {
            if (itemSentiment.llm_sentiment) {
              setShowSentimentModal(datasetIdx + 1);
              setSelectedSentiment({
                questionNumber,
                datasetNumber: datasetIdx + 1,
                data: itemSentiment,
              });
            }
          }}
        >
          {itemSentiment?.sentiment !== undefined &&
            itemSentiment?.sentiment != null
            ? (isPropensity
              ? `${mapTitle("Sentiment")}: ${itemSentiment?.sentiment}`
              : `Sentiment: ${itemSentiment?.sentiment}`)
            : getSentimentIcon(itemSentiment.llm_sentiment)}
        </span>
      </TableCell>
    );
  }

  function renderRow(item, index) {
    const handleQuestionClick = () => {
      let aggregatedSources = [
        ...new Set(
          item.flatMap((itemSentiment) => itemSentiment.sources || [])
        ),
      ];

      aggregatedSources =
        aggregatedSources?.filter((source) => !source.includes("vertexaisearch")) || [];

      if (selectedInput?.llm?.toLowerCase().includes("search")) {
        setShowSourcesModal(true);
      }
      setSelectedSources({
        number: index + 1,
        question: getCleanQuestion(item[0]?.question),
        sources: aggregatedSources,
      });
    };

    return (
      <TableRow
        key={index}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <TableCell style={{ width: "400px" }} component="th" scope="row">
          <Typography
            variant="body2"
            sx={{ cursor: "pointer" }}
            onClick={handleQuestionClick}
          >
            {Array.isArray(item) && item.length
              ? `${index + 1}. ${getCleanQuestion(item[0].question)}`
              : index + 1}
          </Typography>
        </TableCell>

        {hasFactorColumn && (
          <TableCell align="center">{item?.[0]?.factor || "—"}</TableCell>
        )}

        {item.map((itemSentiment, idx) => renderDatasetCell(itemSentiment, idx, index + 1))}
      </TableRow>
    );
  }

  // Simplified renderRow for reporting modal (no click handlers)
  function renderRowForReporting(item, index) {
    return (
      <TableRow
        key={index}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <TableCell style={{ width: "400px" }} component="th" scope="row">
          <Typography variant="body2">
            {Array.isArray(item) && item.length
              ? `${index + 1}. ${getCleanQuestion(item[0].question)}`
              : index + 1}
          </Typography>
        </TableCell>

        {hasFactorColumn && (
          <TableCell align="center">{item?.[0]?.factor || "—"}</TableCell>
        )}

        {item.map((itemSentiment, idx) => {
          return (
            <TableCell align="center" key={idx}>
              <span className={classes.sentiment_emoji}>
                {itemSentiment?.sentiment !== undefined &&
                  itemSentiment?.sentiment != null
                  ? (isPropensity ? `${mapTitle("Sentiment")}: ${itemSentiment?.sentiment}` : `Sentiment: ${itemSentiment?.sentiment}`)
                  : getSentimentIcon(itemSentiment.llm_sentiment)}
              </span>
            </TableCell>
          );
        })}
      </TableRow>
    );
  }

  function renderHeader(sentiments) {
    return (
      <TableHead className={classes.table_header}>
        <TableRow>
          <TableCell>Questions</TableCell>
          {hasFactorColumn && <TableCell align="center">Factor</TableCell>}
          {sentiments.length &&
            sentiments[0].map((_, i) => (
              <TableCell align="center" key={i}>Dataset {i + 1}</TableCell>
            ))}
        </TableRow>
      </TableHead>
    );
  }

  const pdfRef = useRef();

  const exportToPDF = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: `${selectedInput?.brand || selectedInput?.product || 'brand'}_sentiment_questions.pdf`,
    removeAfterPrint: true,
    pageStyle: `
            @page {
              size: 380mm 250mm;
              margin-left: 10mm;
              margin-right: 10mm;
              margin-bottom: 5mm;
              margin-top: 15mm;
            }
            @media print {
            .parent-container {
        overflow: hidden;
      }
              body {
                -webkit-print-color-adjust: exact;
              }
              .page-footer {
                position: fixed;
                bottom: 10mm;
                left: 10mm;
                right: 10mm;
                font-size: 20pt;
                color: #777;
                text-align: center;
              }
            }
          `,
  });

  function renderSentimentQuestionsByFactor(sentiment) {
    const factorOrder = [];
    const rowsByFactor = {};
    sentiment.forEach((group, questionIndex) => {
      const factor = group?.[0]?.factor || "—";
      if (!rowsByFactor[factor]) {
        rowsByFactor[factor] = [];
        factorOrder.push(factor);
      }
      rowsByFactor[factor].push({ group, questionIndex });
    });

    const datasetCount = sentiment[0]?.length || 0;
    const normalizedScoreFor = (factor) =>
      data?.favorabilities?.[0]?.sentimentScoreByFactor?.[factor]?.normalizedScore;

    return (
      <Table sx={{ minWidth: 800 }} aria-label="sentiment questions by factor">
        <TableHead className={classes.table_header}>
          <TableRow>
            <TableCell>Question</TableCell>
            {Array.from({ length: datasetCount }).map((_, i) => (
              <TableCell align="center" key={i}>Dataset {i + 1}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {factorOrder.map((factor) => (
            <Fragment key={factor}>
              <StyledTableRow>
                <TableCell
                  colSpan={datasetCount + 1}
                  sx={{ background: "#f4f6f8", fontWeight: 700, fontSize: 13 }}
                >
                  <span>{factor !== "—" ? `${factor} — ` : ""}Sentiment Score (0–50): {normalizedScoreFor(factor) ?? "—"}</span>
                </TableCell>
              </StyledTableRow>
              {rowsByFactor[factor].map(({ group, questionIndex }) => (
                <TableRow
                  key={questionIndex}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell style={{ width: "400px" }} component="th" scope="row">
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          background: "#e6faf7",
                          color: "#12857a",
                          fontWeight: 700,
                          borderRadius: 6,
                          padding: "2px 8px",
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        Q{questionIndex + 1}
                      </span>
                      <Typography variant="body2">
                        {getCleanQuestion(group?.[0]?.question)}
                      </Typography>
                    </span>
                  </TableCell>
                  {group.map((itemSentiment, di) => renderDatasetCell(itemSentiment, di, questionIndex + 1))}
                </TableRow>
              ))}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    );
  }

  function renderSentimentScores(sentiment) {
    if (showScore && sentiment?.length > 0) {
      // Prepare all rows: main brand + competitors (if fromDashboardBrandScore)
      const allRows = [];
      const competitorRows = []; // Track competitor rows separately
      const addedRowNames = new Set(); // Track added row names to prevent duplicates

      const sentimentMetaByName = {};

      const scopeFactors = hasFactorColumn
        ? new Set(sentiment.map((g) => g?.[0]?.factor).filter(Boolean))
        : null;
      const scopeToFactors = (meta) =>
        scopeFactors ? (meta || []).filter((g) => scopeFactors.has(g?.[0]?.factor)) : meta;

      // Add main brand row
      if (data?.favorabilities && data?.favorabilities.length > 0) {
        data.favorabilities.forEach((fav) => {
          if (fav?.name && !addedRowNames.has(fav.name)) {
            allRows.push(fav);
            addedRowNames.add(fav.name);
            if (data?.sentimentMeta) sentimentMetaByName[fav.name] = data.sentimentMeta;
          }
        });
      }

      // Add competitor rows if from Dashboard BrandScore
      if (fromDashboardBrandScore && compIndexScoreMetaData?.length > 0) {
        compIndexScoreMetaData.forEach((compMeta) => {
          // Skip if it's the main brand
          if (compMeta?.name === brandProduct) return;

          const compData = compMeta?.extras || compMeta;
          const compFavorabilities = compData?.favorabilities || [];

          if (compFavorabilities.length > 0) {
            compFavorabilities.forEach((fav) => {
              if (fav?.name && !addedRowNames.has(fav.name)) {
                allRows.push(fav);
                addedRowNames.add(fav.name);
                competitorRows.push(fav);
                if (compData?.sentimentMeta) sentimentMetaByName[fav.name] = scopeToFactors(compData.sentimentMeta);
              }
            });
          }
        });


        // Calculate Competitor Average row (excluding brand scores)
        if (competitorRows.length > 0) {
          const competitorAverageRow = { name: "Competitor Average" };

          // Get number of sentiment columns
          const sentimentKeys = Object.keys(competitorRows[0]).filter(key => key.startsWith("sentiment"));

          // Calculate average for each sentiment column (competitors only)
          sentimentKeys.forEach((key, index) => {
            const sum = competitorRows.reduce((acc, row) => {
              const value = parseFloat(row[key]);
              return acc + (isNaN(value) ? 0 : value);
            }, 0);
            const avg = competitorRows.length > 0 ? sum / competitorRows.length : 0;
            competitorAverageRow[key] = avg.toFixed(2);
          });

          // Calculate average of the Avg column (competitors only)
          const avgSum = competitorRows.reduce((acc, row) => {
            const value = parseFloat(row.average);
            return acc + (isNaN(value) ? 0 : value);
          }, 0);
          competitorAverageRow.average = (competitorRows.length > 0 ? avgSum / competitorRows.length : 0).toFixed(2);

          // Calculate average of score column if it exists (competitors only)
          if (competitorRows[0].score !== undefined) {
            const scoreSum = competitorRows.reduce((acc, row) => {
              const value = parseFloat(row.score);
              return acc + (isNaN(value) ? 0 : value);
            }, 0);
            competitorAverageRow.score = (competitorRows.length > 0 ? scoreSum / competitorRows.length : 0).toFixed(2);
          }

          // Average each factor's score across competitors too, so the
          // Factor Avg columns aren't blank for this row.
          if (competitorRows[0].sentimentScoreByFactor) {
            const factorKeys = Object.keys(competitorRows[0].sentimentScoreByFactor);
            competitorAverageRow.sentimentScoreByFactor = {};
            factorKeys.forEach((factor) => {
              const vals = competitorRows
                .map((r) => parseFloat(r.sentimentScoreByFactor?.[factor]?.score))
                .filter((v) => !isNaN(v));
              competitorAverageRow.sentimentScoreByFactor[factor] = {
                score: vals.length ? Number((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(3)) : null,
              };
            });
          }
          // Keep the underlying competitor names so the per-question columns
          // can show an average-of-averages instead of "—".
          competitorAverageRow._competitorNames = competitorRows.map((r) => r.name);

          // Add Competitor Average row at the end
          allRows.push(competitorAverageRow);
        }
      }


      // Prepare data for color coding
      const sentimentColumnCount = data?.favorabilities?.[0]
        ? Object.keys(data?.favorabilities[0]).filter(key => key.startsWith("sentiment")).length
        : 0;
      
      const isExclude = true; // Set to false to include Competitor Average in color comparison
      
      // Filter rows for color comparison based on isExclude flag
      const rowsForColorComparison = isExclude 
        ? allRows.filter(row => row.name !== "Competitor Average")
        : allRows;
      
      // Get all values for each sentiment column for color comparison
      const sentimentColumnValues = {};
      for (let i = 0; i < sentimentColumnCount; i++) {
        const colKey = `sentiment${i + 1}`;
        sentimentColumnValues[colKey] = rowsForColorComparison
          .map(row => row[colKey])
          .filter(val => val !== undefined && val !== null);
      }

      // Get all average values for color comparison
      const averageValues = rowsForColorComparison
        .map(row => row.average)
        .filter(val => val !== undefined && val !== null);

      // When a custom index score type tagged questions with factors, group
      // the per-question columns by factor instead of showing Question 1..N.
      const factorNames = data?.favorabilities?.[0]?.sentimentScoreByFactor
        ? Object.keys(data.favorabilities[0].sentimentScoreByFactor)
        : null;

      const factorColumnValues = {};
      if (factorNames) {
        factorNames.forEach((factor) => {
          factorColumnValues[factor] = rowsForColorComparison
            .map((row) => row.sentimentScoreByFactor?.[factor]?.score)
            .filter((val) => val !== undefined && val !== null);
        });
      }

      const factorQuestionGroups = {};
      if (factorNames) {
        sentiment.forEach((group, questionIndex) => {
          const factor = group?.[0]?.factor;
          if (!factor) return;
          if (!factorQuestionGroups[factor]) factorQuestionGroups[factor] = [];
          factorQuestionGroups[factor].push({ group, questionIndex });
        });
      }

      const orderedFactorNames = factorNames
        ? [...factorNames].sort((a, b) => {
            const aMin = Math.min(...(factorQuestionGroups[a]?.map((q) => q.questionIndex) ?? [Infinity]));
            const bMin = Math.min(...(factorQuestionGroups[b]?.map((q) => q.questionIndex) ?? [Infinity]));
            return aMin - bMin;
          })
        : null;

      // Scoped to a single factor (e.g. the embedded per-factor breakups
      // view) — the factor name is already shown above this table, so skip
      // the merged factor header/sub-header row and just show its questions
      // directly, with its own average taking over the single "Avg" column.
      const isSingleFactor = !!(factorNames && factorNames.length === 1);
      const singleFactorHeaderRowSpan = factorNames && !isSingleFactor ? 2 : 1;

      const questionAverage = (group) => {
        const vals = (group || [])
          .map((item) => parseFloat(item?.llm_sentiment_score ?? item?.sentiment))
          .filter((v) => !isNaN(v));
        return vals.length ? Number((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)) : null;
      };

      const questionCellValue = (row, questionIndex) => {
        if (row._competitorNames) {
          const vals = row._competitorNames
            .map((name) => questionAverage(sentimentMetaByName[name]?.[questionIndex]))
            .filter((v) => v !== null);
          return vals.length ? Number((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)) : null;
        }
        return questionAverage(sentimentMetaByName[row.name]?.[questionIndex]);
      };

      const questionColumnValues = {};
      if (factorNames) {
        sentiment.forEach((_, questionIndex) => {
          questionColumnValues[questionIndex] = rowsForColorComparison
            .map((row) => questionCellValue(row, questionIndex))
            .filter((val) => val !== undefined && val !== null);
        });
      }

      return (
        <>
          <hr />
          <h6>{scoresHeading ?? `B. ${isPropensity ? mapTitle("Average Sentiment Scores") : "Average Sentiment Scores"}`}</h6>
          <hr />
          <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: factorNames && !isSingleFactor ? 1100 : 800 }} aria-label="simple table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell rowSpan={singleFactorHeaderRowSpan} sx={{ borderRight: "1px solid rgba(224,224,224,1)" }}>Name</StyledTableCell>
                {factorNames
                  ? (isSingleFactor
                      ? (factorQuestionGroups[orderedFactorNames[0]] || []).map(({ questionIndex }) => (
                          <StyledTableCell align="center" key={`q-${questionIndex}`}>
                            Question {questionIndex + 1}
                          </StyledTableCell>
                        ))
                      : orderedFactorNames.map((factor) => (
                          <StyledTableCell
                            align="center"
                            colSpan={(factorQuestionGroups[factor]?.length || 0) + 1}
                            key={factor}
                            sx={{ borderRight: "1px solid rgba(224,224,224,1)" }}
                          >
                            {factor}
                          </StyledTableCell>
                        )))
                  : data?.favorabilities &&
                    data?.favorabilities.length > 0 &&
                    Array.from({
                      length: Object.keys(data?.favorabilities?.[0])?.filter((key) =>
                        key.startsWith("sentiment")
                      ).length,
                    }).map((_, i) => (
                      <StyledTableCell align="right" key={i}>
                        Question {i + 1}
                      </StyledTableCell>
                    ))}
                <StyledTableCell align="right" rowSpan={singleFactorHeaderRowSpan}>Avg</StyledTableCell>
                {data?.favorabilities &&
                  data?.favorabilities.length > 0 &&
                  Object.keys(data?.favorabilities[0]).includes("score") && (
                    <StyledTableCell rowSpan={singleFactorHeaderRowSpan}>Score</StyledTableCell>
                  )}
              </StyledTableRow>
              {factorNames && !isSingleFactor && (
                <StyledTableRow>
                  {orderedFactorNames.map((factor) => (
                    <Fragment key={`${factor}-sub`}>
                      {(factorQuestionGroups[factor] || []).map(({ questionIndex }) => (
                        <StyledTableCell align="center" key={`${factor}-q${questionIndex}`} sx={{ fontSize: 11, fontWeight: 600 }}>
                          Q{questionIndex + 1}
                        </StyledTableCell>
                      ))}
                      <StyledTableCell align="center" key={`${factor}-avg`} sx={{ fontSize: 11, fontWeight: 700, borderRight: "1px solid rgba(224,224,224,1)" }}>
                        Factor Avg
                      </StyledTableCell>
                    </Fragment>
                  ))}
                </StyledTableRow>
              )}
            </TableHead>
            <TableBody>
              {allRows?.map((row, idx) => {
                const isCompetitorAverage = row.name === "Competitor Average";
                return (
                  <StyledTableRow
                    key={`${row.name}-${idx}`}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      backgroundColor: isCompetitorAverage ? "#e3f2fd" : "inherit",
                      fontWeight: isCompetitorAverage ? "bold" : "normal"
                    }}
                  >
                    <StyledTableCell
                      component="th"
                      scope="row"
                      sx={{ fontWeight: isCompetitorAverage ? "bold" : "normal", borderRight: factorNames ? "1px solid rgba(224,224,224,1)" : "none" }}
                    >
                      {row.name}
                    </StyledTableCell>
                    {factorNames
                      ? (isSingleFactor
                          ? (factorQuestionGroups[orderedFactorNames[0]] || []).map(({ questionIndex }) => {
                              const qValue = questionCellValue(row, questionIndex);
                              const qColor = (!isCompetitorAverage || !isExclude)
                                ? getColorForScore(qValue, questionColumnValues[questionIndex])
                                : 'inherit';
                              return (
                                <StyledTableCell
                                  align="center"
                                  key={`q${questionIndex}`}
                                  sx={{ fontWeight: isCompetitorAverage ? "bold" : "normal", color: qColor }}
                                >
                                  {qValue ?? "—"}
                                </StyledTableCell>
                              );
                            })
                          : orderedFactorNames.map((factor) => {
                              const cellValue = row.sentimentScoreByFactor?.[factor]?.score;
                              const cellColor = (!isCompetitorAverage || !isExclude)
                                ? getColorForScore(cellValue, factorColumnValues[factor])
                                : 'inherit';
                              return (
                                <Fragment key={factor}>
                                  {(factorQuestionGroups[factor] || []).map(({ questionIndex }) => {
                                    const qValue = questionCellValue(row, questionIndex);
                                    const qColor = (!isCompetitorAverage || !isExclude)
                                      ? getColorForScore(qValue, questionColumnValues[questionIndex])
                                      : 'inherit';
                                    return (
                                      <StyledTableCell
                                        align="center"
                                        key={`${factor}-q${questionIndex}`}
                                        sx={{ fontWeight: isCompetitorAverage ? "bold" : "normal", color: qColor }}
                                      >
                                        {qValue ?? "—"}
                                      </StyledTableCell>
                                    );
                                  })}
                                  <StyledTableCell
                                    align="right"
                                    key={`${factor}-avg`}
                                    sx={{
                                      fontWeight: 700,
                                      color: cellColor,
                                      borderRight: "1px solid rgba(224,224,224,1)"
                                    }}
                                  >
                                    {cellValue ?? "—"}
                                  </StyledTableCell>
                                </Fragment>
                              );
                            }))
                      : data?.favorabilities &&
                        data?.favorabilities.length > 0 &&
                        Array.from({
                          length: Object.keys(data?.favorabilities?.[0])?.filter(
                            (key) => key.startsWith("sentiment")
                          ).length,
                        })?.map((_, i) => {
                          const colKey = `sentiment${i + 1}`;
                          const cellValue = row[colKey];
                          const cellColor = (!isCompetitorAverage || !isExclude)
                            ? getColorForScore(cellValue, sentimentColumnValues[colKey])
                            : 'inherit';
                          return (
                            <StyledTableCell
                              align="right"
                              key={i}
                              sx={{
                                fontWeight: isCompetitorAverage ? "bold" : "normal",
                                color: cellColor
                              }}
                            >
                              {cellValue}
                            </StyledTableCell>
                          );
                        })}
                    <StyledTableCell
                      align="right"
                      sx={{
                        fontWeight: isCompetitorAverage ? "bold" : "normal",
                        color: (!isCompetitorAverage || !isExclude)
                          ? getColorForScore(
                              isSingleFactor ? row.sentimentScoreByFactor?.[orderedFactorNames[0]]?.score : row.average,
                              isSingleFactor ? factorColumnValues[orderedFactorNames[0]] : averageValues
                            )
                          : 'inherit'
                      }}
                    >
                      {isSingleFactor ? (row.sentimentScoreByFactor?.[orderedFactorNames[0]]?.score ?? "—") : row.average}
                    </StyledTableCell>
                    {row.score && (
                      <StyledTableCell
                        align="right"
                        sx={{ fontWeight: isCompetitorAverage ? "bold" : "normal" }}
                      >
                        {row?.score}
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
          </TableContainer>
        </>
      );
    }
  }

  return (
    <>
      <div id={`sentiment_id${idSuffix}`}>
        {showScore && !embedded && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h6>A. {isPropensity ? mapTitle("Sentiment Analysis") : "Sentiment Analysis"}</h6>
              <div style={{ display: "flex", gap: "12px" }}>
                <DownloadIcon onClick={exportToPDF} style={{ cursor: "pointer" }} />
              </div>
            </div>
            <hr />
          </>
        )}

        {isReporting && !embedded && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DescriptionIcon />}
              onClick={() => setShowReportingModal(true)}
              style={{ textTransform: "none" }}
            >
              {/* Sentiment Report View */}
              {reportviewButtontext}
            </Button>
          </div>
        )}

        <div style={{ display: "flex" }} id={`print-pdf${idSuffix}`} ref={pdfRef}>
          {hasFactorColumn ? (
            renderSentimentQuestionsByFactor(sentiments)
          ) : (
            <MUITable
              size="medium"
              elevation={0}
              data={sentiments}
              render={renderRow}
              renderHeaders={() => renderHeader(sentiments)}
            />
          )}
        </div>
      </div>

      {/* Reporting Modal - Paginated View */}
      <Modal
        handleClose={() => setShowReportingModal(false)}
        open={showReportingModal}
        title={`${mapTitle("Sentiment Analysis")} - Report View (Paginated for PPT)`}
        width={1200}
      // height={800}
      >
        <div style={{ maxHeight: "70vh", overflowY: "auto", padding: "8px" }}>
          <PaginatedSentimentTable
            data={data}
            renderRow={renderRowForReporting}
            renderHeader={renderHeader}
            maxPageHeight={600}
          />
        </div>
      </Modal>

      {/* Sources Modal */}
      <Modal
        handleClose={() => setShowSourcesModal(false)}
        open={showSourcesModal && selectedInput?.llm?.toLowerCase().includes("search")}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>Data Sources (Aggregated)</span>
            &nbsp; &nbsp;
            <DownloadIcon
              style={{ cursor: "pointer" }}
              onClick={() =>
                downloadExcel(
                  selectedSources?.sources,
                  "Sentiment Questions",
                  selectedInput,
                  selectedSources?.question
                )
              }
            />
          </div>
        }
        width={850}
      >
        {selectedSources?.sources?.length ? (
          <div style={{ maxHeight: "400px", overflowY: "auto", padding: "8px" }}>
            <ul>
              {selectedSources.sources.map((src, i) => (
                <li key={i}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, wordBreak: "break-word" }}
                  >
                    <a href={src} target="_blank" rel="noopener noreferrer">
                      {src}
                    </a>
                  </Typography>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No sources available.
          </Typography>
        )}
      </Modal>

      {/* Sentiment Modal */}
     <Modal
        handleClose={() => setShowSentimentModal(false)}
        open={showSentimentModal}
        title={`${mapTitle("Sentiment")}: Question ${selectedSentiment?.questionNumber} (Dataset ${selectedSentiment?.datasetNumber})`}
        width={850}
      >
        <SentimentItem 
          selectedInput={selectedInput} 
          sentiment={selectedSentiment?.data}
          showInfoIcons={showInfoIcon}
          onQuestionInfoClick={handleSentimentQuestionClick}
          onExplanationInfoClick={handleSentimentAnalysisClick}
        />
      </Modal>
       <BaseResponsesModal
        open={sentimentInfoModalOpen}
        title={sentimentInfoModalTitle}
        content={sentimentInfoModalContent}
        onClose={closeSentimentInfoModal}
      />

      {showScore && renderSentimentScores(sentiments)}
    </>
  );
}